/**
 * EventsTab - Unified timeline of entity lifecycle events
 *
 * Time periods:
 * - Ce mois (current month)
 * - Mois précédent (last month)
 * - Cette année (this year)
 * - Année précédente (last year)
 *
 * Features:
 * - Clickable pills for each event type
 * - Group by theme with cards/squared buttons
 * - Accordion to condensate
 * - Click to drive to monthly list
 *
 * ALL ACCORDIONS CLOSED BY DEFAULT
 */
import React, { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrgCurrency } from '../../../hooks/useOrgCurrency.js';
import { useFormat } from '../../../i18n/format.js';
import {
  Wrench, ArrowRightLeft, ClipboardCheck, Fuel, AlertTriangle,
  FileText, MapPin, Calendar, Clock, CheckCircle, XCircle,
  AlertCircle, PlayCircle, ChevronLeft
} from 'lucide-react';
import { useEntityEvents, EVENT_TYPES } from '../../../hooks/useEntityEvents.js';
import { useAccordionState } from '../../../hooks/useAccordionState.js';
import { AccordionGroup, AccordionContainer } from '../AccordionGroup.jsx';
import { EmptyState } from '../EntityDetailModal.jsx';

// Icon mapping
const ICON_MAP = {
  Wrench,
  ArrowsRightLeft: ArrowRightLeft,
  ClipboardCheck,
  Fuel,
  AlertTriangle,
  FileText,
  MapPin
};

// Status configuration for events. Labels are NOT stored here (i18n piege #1: a module
// constant is evaluated once, before any language switch) — resolved at render via t().
const STATUS_CONFIG = {
  draft: { color: 'bg-gray-100 text-gray-800', icon: Clock },
  pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  in_progress: { color: 'bg-blue-100 text-blue-800', icon: PlayCircle },
  completed: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
  cancelled: { color: 'bg-gray-100 text-gray-500', icon: XCircle },
  rejected: { color: 'bg-red-100 text-red-800', icon: XCircle },
  active: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
  planned: { color: 'bg-blue-100 text-blue-800', icon: Calendar },
  submitted: { color: 'bg-blue-100 text-blue-800', icon: Clock },
  manager_review: { color: 'bg-yellow-100 text-yellow-800', icon: AlertCircle },
  approved: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
  declared: { color: 'bg-blue-100 text-blue-800', icon: AlertTriangle },
  acknowledged: { color: 'bg-purple-100 text-purple-800', icon: CheckCircle },
  expert_pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  closed: { color: 'bg-gray-100 text-gray-800', icon: CheckCircle },
  validated: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
  invalid: { color: 'bg-red-100 text-red-800', icon: XCircle }
};

// Status → i18n key. `cancelled`, `closed` and `planned` reuse the shared `events.status.*`
// wording (identical text); the rest are specific to this workflow-status set.
const STATUS_LABEL_KEY = {
  draft: 'common.tabs.eventsTab.status.draft',
  pending: 'common.tabs.eventsTab.status.pending',
  in_progress: 'common.tabs.eventsTab.status.inProgress',
  completed: 'common.tabs.eventsTab.status.completed',
  cancelled: 'events.status.cancelled',
  rejected: 'common.tabs.eventsTab.status.rejected',
  active: 'common.tabs.eventsTab.status.active',
  planned: 'events.status.planned',
  submitted: 'common.tabs.eventsTab.status.submitted',
  manager_review: 'common.tabs.eventsTab.status.managerReview',
  approved: 'common.tabs.eventsTab.status.approved',
  declared: 'common.tabs.eventsTab.status.declared',
  acknowledged: 'common.tabs.eventsTab.status.acknowledged',
  expert_pending: 'common.tabs.eventsTab.status.expertPending',
  closed: 'events.status.closed',
  validated: 'common.tabs.eventsTab.status.validated',
  invalid: 'common.tabs.eventsTab.status.invalid'
};

// Time period configuration. Labels resolved at render via t().
const TIME_PERIODS = {
  current_month: { color: 'blue' },
  previous_month: { color: 'slate' },
  current_year: { color: 'emerald' },
  previous_year: { color: 'gray' }
};

const TIME_PERIOD_LABEL_KEY = {
  current_month: 'common.tabs.eventsTab.periods.currentMonth',
  previous_month: 'common.tabs.eventsTab.periods.previousMonth',
  current_year: 'common.tabs.eventsTab.periods.currentYear',
  previous_year: 'common.tabs.eventsTab.periods.previousYear'
};

// Get time period for an event
function getTimePeriod(date) {
  if (!date) return 'previous_year';

  const eventDate = new Date(date);
  const now = new Date();
  const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const thisYear = new Date(now.getFullYear(), 0, 1);
  const lastYear = new Date(now.getFullYear() - 1, 0, 1);

  if (eventDate >= thisMonth) {
    return 'current_month';
  } else if (eventDate >= lastMonth) {
    return 'previous_month';
  } else if (eventDate >= thisYear) {
    return 'current_year';
  } else if (eventDate >= lastYear) {
    return 'previous_year';
  }
  return 'previous_year';
}

// Event card component (for list view)
function EventCard({ event }) {
  // Devise de l'organisation de l'evenement - un seul outil monetaire.
  const { money, currencyOf } = useOrgCurrency();
  const { t, fmt } = useFormat();
  const typeConfig = EVENT_TYPES[event.eventType] || {};
  const Icon = ICON_MAP[typeConfig.icon] || FileText;
  const statusKey = event.status || typeConfig.getStatus?.(event);
  const statusConfig = STATUS_CONFIG[statusKey] || STATUS_CONFIG.pending;
  const statusLabel = t(STATUS_LABEL_KEY[statusKey] || STATUS_LABEL_KEY.pending);
  const StatusIcon = statusConfig.icon;
  const formatDate = (date) => (date ? fmt.date(date, { day: '2-digit', month: 'short', year: 'numeric' }) : t('common.states.notAvailable'));

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-3 hover:shadow-sm transition-shadow">
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-lg bg-${typeConfig.color || 'gray'}-100`}>
          <Icon className={`w-4 h-4 text-${typeConfig.color || 'gray'}-600`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-medium text-gray-500 uppercase">
              {typeConfig.label || event.eventType}
            </span>
            <span className={`px-2 py-0.5 text-xs rounded-full flex items-center gap-1 ${statusConfig.color}`}>
              <StatusIcon className="w-3 h-3" />
              {statusLabel}
            </span>
          </div>
          <p className="text-sm font-medium text-gray-900 mt-1 truncate">
            {typeConfig.getTitle?.(event, currencyOf(event)) || event.description || t('common.timeline.types.event')}
          </p>
          <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
            <Calendar className="w-3 h-3" />
            {formatDate(event.eventDate)}
            {event.eventType === 'maintenance' && event.total_cost && (
              <span className="ml-2 text-amber-600 font-medium">{money(event.total_cost, event)}</span>
            )}
            {event.eventType === 'fuel' && (
              <span className="ml-2 text-orange-600 font-medium">{event.liters}L</span>
            )}
            {event.eventType === 'claim' && event.estimated_amount && (
              <span className="ml-2 text-red-600 font-medium">{money(event.estimated_amount, event)}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Event type pill/button (clickable)
function EventTypePill({ eventType, count, onClick, isActive }) {
  const typeConfig = EVENT_TYPES[eventType] || {};
  const Icon = ICON_MAP[typeConfig.icon] || FileText;

  if (count === 0) return null;

  return (
    <button
      onClick={onClick}
      className={`flex flex-col items-center justify-center p-3 rounded-lg border-2 transition-all min-w-[80px] ${
        isActive
          ? `border-${typeConfig.color || 'gray'}-500 bg-${typeConfig.color || 'gray'}-100 shadow-md`
          : `border-gray-200 bg-white hover:border-${typeConfig.color || 'gray'}-300 hover:bg-${typeConfig.color || 'gray'}-50`
      }`}
    >
      <Icon className={`w-6 h-6 text-${typeConfig.color || 'gray'}-600 mb-1`} />
      <span className="text-xs font-medium text-gray-700">{typeConfig.label}</span>
      <span className={`mt-1 px-2 py-0.5 text-xs rounded-full bg-${typeConfig.color || 'gray'}-100 text-${typeConfig.color || 'gray'}-800 font-semibold`}>
        {count}
      </span>
    </button>
  );
}

// Detailed list view for a period/type
function EventListView({ events, title, onBack }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <button
          onClick={onBack}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <ChevronLeft className="w-5 h-5 text-gray-600" />
        </button>
        <h3 className="text-lg font-bold text-gray-900">{title}</h3>
        <span className="text-sm text-gray-500">({t('common.tabs.eventsTab.eventCount', { count: events.length })})</span>
      </div>
      <div className="space-y-2">
        {events.length === 0 ? (
          <p className="text-sm text-gray-500 italic text-center py-8">{t('common.tabs.eventsTab.noEvents')}</p>
        ) : (
          events.map((event, index) => (
            <EventCard key={event.id || index} event={event} />
          ))
        )}
      </div>
    </div>
  );
}

// Period summary with event type pills
function PeriodSummary({ periodKey, periodData, onSelectType }) {
  const eventTypes = Object.keys(EVENT_TYPES);

  // Count events by type for this period
  const typeCounts = useMemo(() => {
    const counts = {};
    eventTypes.forEach(type => {
      counts[type] = (periodData.byType[type] || []).length;
    });
    return counts;
  }, [periodData, eventTypes]);

  const hasEvents = Object.values(typeCounts).some(c => c > 0);

  if (!hasEvents) return null;

  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
      {eventTypes.map(type => (
        <EventTypePill
          key={type}
          eventType={type}
          count={typeCounts[type]}
          onClick={() => onSelectType(periodKey, type)}
          isActive={false}
        />
      ))}
    </div>
  );
}

export function EventsTab({ entity, entityType, onMessage }) {
  const { t } = useTranslation();
  // View state: null = summary view, { period, type } = list view
  const [selectedView, setSelectedView] = useState(null);

  // Fetch events
  const { events, loading, error, reload } = useEntityEvents(entityType, entity?.id);

  // Accordion state - ALL CLOSED by default
  const accordion = useAccordionState({ defaultExpanded: [] });

  // Group events by time period and type
  const groupedEvents = useMemo(() => {
    const groups = {};
    Object.keys(TIME_PERIODS).forEach(period => {
      groups[period] = {
        events: [],
        byType: {}
      };
      Object.keys(EVENT_TYPES).forEach(type => {
        groups[period].byType[type] = [];
      });
    });

    events.forEach(event => {
      const period = getTimePeriod(event.eventDate);
      groups[period].events.push(event);
      if (groups[period].byType[event.eventType]) {
        groups[period].byType[event.eventType].push(event);
      }
    });

    return groups;
  }, [events]);

  // Event counts
  const eventCounts = useMemo(() => {
    const counts = { total: events.length };
    Object.keys(EVENT_TYPES).forEach(type => {
      counts[type] = events.filter(e => e.eventType === type).length;
    });
    return counts;
  }, [events]);

  // Handle selecting a type to view
  const handleSelectType = useCallback((period, type) => {
    setSelectedView({ period, type });
  }, []);

  // Handle going back to summary
  const handleBack = useCallback(() => {
    setSelectedView(null);
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-600">{t('common.tabs.eventsTab.loading')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 bg-red-50 rounded-lg">
        <AlertTriangle className="w-12 h-12 text-red-300 mx-auto mb-2" />
        <p className="text-red-600">{error}</p>
        <button onClick={reload} className="mt-2 text-sm text-red-600 hover:text-red-800 underline">
          {t('common.actions.retry')}
        </button>
      </div>
    );
  }

  if (eventCounts.total === 0) {
    return (
      <EmptyState
        icon={Calendar}
        title={t('common.tabs.eventsTab.noEvents')}
        message={t('common.tabs.eventsTab.noEventsForItem')}
      />
    );
  }

  // If viewing a specific type list
  if (selectedView) {
    const { period, type } = selectedView;
    const periodConfig = TIME_PERIODS[period];
    const typeConfig = EVENT_TYPES[type];
    const periodLabel = TIME_PERIOD_LABEL_KEY[period] ? t(TIME_PERIOD_LABEL_KEY[period]) : period;
    const filteredEvents = groupedEvents[period]?.byType[type] || [];
    const title = `${typeConfig?.label || type} - ${periodConfig ? periodLabel : period}`;

    return (
      <EventListView
        events={filteredEvents}
        title={title}
        onBack={handleBack}
      />
    );
  }

  // Summary view with accordions
  const periodOrder = ['current_month', 'previous_month', 'current_year', 'previous_year'];

  return (
    <div className="space-y-4">
      {/* Header with total count */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-gray-900">{t('common.tabs.eventsTab.title')}</h3>
          <span className="text-sm text-gray-500">
            {t('common.tabs.eventsTab.eventCount', { count: eventCounts.total })}
          </span>
        </div>
        {/* Summary pills for all event types */}
        <div className="flex flex-wrap gap-2 mt-3">
          {Object.entries(eventCounts).map(([type, count]) => {
            if (type === 'total' || count === 0) return null;
            const typeConfig = EVENT_TYPES[type];
            if (!typeConfig) return null;
            return (
              <span
                key={type}
                className={`px-2 py-1 text-xs rounded-full bg-${typeConfig.color}-100 text-${typeConfig.color}-800 cursor-default`}
              >
                {typeConfig.label}: {count}
              </span>
            );
          })}
        </div>
      </div>

      {/* Period Accordions */}
      <AccordionContainer>
        {periodOrder.map(periodKey => {
          const periodData = groupedEvents[periodKey];
          const periodConfig = TIME_PERIODS[periodKey];
          const eventCount = periodData?.events.length || 0;

          if (eventCount === 0) return null;

          return (
            <AccordionGroup
              key={periodKey}
              id={periodKey}
              title={t(TIME_PERIOD_LABEL_KEY[periodKey])}
              count={eventCount}
              icon={Calendar}
              color={periodConfig.color}
              isExpanded={accordion.isExpanded(periodKey)}
              onToggle={() => accordion.toggleGroup(periodKey)}
            >
              <PeriodSummary
                periodKey={periodKey}
                periodData={periodData}
                onSelectType={handleSelectType}
              />
            </AccordionGroup>
          );
        })}
      </AccordionContainer>
    </div>
  );
}

export default EventsTab;
